// Size & Colour reports - all 16 (handover section 6.6) on one page:
// /Reports/SizeColourReport/:code. Each runs POST SizeColour/Report/{code}
// with the filter body and gets back { resultName: [rows] }; every result set
// is a tab, its columns come from the rows, and the KPI chips are summed from
// the rows. SC-40 can mark the picked variants' labels as printed.
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchCategory, fetchProduct } from "../../../redux/userSetting";
import {
  getColours,
  getSizeColourReport,
  getSizeSets,
  getSubCategories,
  setLabelPrinted,
} from "../../../services/SizeColourApiServices";
import { all_routes } from "../../../Router/all_routes";
import {
  AdminScreen,
  AxField,
  AxRefreshIcon,
  AxSelectField,
  AxStatusKeys,
  AxTabs,
  AxTodoNote,
} from "../../../components/adminsitration/ui";

// code, name, required filters, extra filters
export const SC_REPORTS = [
  { code: "SC-01", name: "Stock Matrix", group: "Stock" },
  { code: "SC-02", name: "Size-wise Stock", group: "Stock", req: ["sizeSetID"] },
  { code: "SC-03", name: "Colour-wise Stock", group: "Stock" },
  { code: "SC-04", name: "Variant Stock Ledger", group: "Stock", req: ["variantID", "fromDate", "toDate"] },
  { code: "SC-05", name: "Branch-wise Stock Matrix", group: "Stock", req: ["productID"] },
  { code: "SC-06", name: "Stock Valuation", group: "Stock" },
  { code: "SC-07", name: "Broken Sizes", group: "Stock" },
  { code: "SC-08", name: "Reorder", group: "Stock" },
  { code: "SC-11", name: "Sales Matrix", group: "Sales", req: ["fromDate", "toDate"] },
  { code: "SC-12", name: "Size Curve", group: "Sales", req: ["sizeSetID", "fromDate", "toDate"] },
  { code: "SC-13", name: "Colour-wise Sales", group: "Sales", req: ["fromDate", "toDate"] },
  { code: "SC-15", name: "Variant Sales Register", group: "Sales", req: ["fromDate", "toDate"] },
  { code: "SC-17", name: "Returns & Exchanges", group: "Sales", req: ["fromDate", "toDate"], extra: ["returnType", "reason"] },
  { code: "SC-39", name: "Variant Master List", group: "Masters" },
  { code: "SC-40", name: "Barcode List", group: "Masters", extra: ["barcodeType", "source"], labels: true },
  { code: "SC-41", name: "Barcode Exceptions", group: "Masters" },
];
const DATED = (r) => (r.req || []).includes("fromDate");
const LABEL = {
  asOnDate: "As on",
  fromDate: "From",
  toDate: "To",
  branchID: "Branch",
  categoryID: "Category",
  subCategoryID: "Sub-category",
  sizeSetID: "Size set",
  colourID: "Colour",
  productID: "Product",
  variantID: "Variant",
  returnType: "Return type",
  reason: "Reason",
  barcodeType: "Barcode type",
  source: "Barcode source",
};

const arr = (x) => (Array.isArray(x) ? x : []);
const errMsg = (e, fallback) => e?.response?.data?.message || e?.response?.data?.Message || fallback;
const titleOf = (k) =>
  String(k)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
const HIDE = /(^id$|ID$|Id$|guid$|Guid$)/;
const ISO = /^\d{4}-\d{2}-\d{2}(T[\d:.]+)?$/;
const HEX = /^#[0-9a-f]{6}$/i;
const fmtNum = (v) => (Number.isInteger(v) ? v.toLocaleString("en-US") : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
// rates and averages are not added up in the totals row
const RATE = /(pct|percent|rate|price|share|ratio|cover|avg|average)/i;

// the columns a result set's rows carry, in the order the first row has them
const columnsOf = (rows) => {
  const keys = [];
  rows.slice(0, 50).forEach((r) => Object.keys(r || {}).forEach((k) => !keys.includes(k) && keys.push(k)));
  return keys
    .filter((k) => !HIDE.test(k))
    .filter((k) => rows.some((r) => typeof r?.[k] !== "object" || r[k] === null))
    .map((k) => {
      const sample = rows.find((r) => r?.[k] !== null && r?.[k] !== undefined)?.[k];
      const num = typeof sample === "number";
      return { k, t: titleOf(k), num, sum: num && !RATE.test(k), hex: /hex/i.test(k) || (typeof sample === "string" && HEX.test(sample)), date: typeof sample === "string" && ISO.test(sample) };
    });
};
const cellText = (c, v) => {
  if (v === null || v === undefined || v === "") return "";
  if (c.num) return fmtNum(Number(v));
  if (c.date) return dayjs(v).format("DD MMM YYYY");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

const SizeColourReports = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { code: routeCode } = useParams();
  const { branch } = useSelector((state) => state.branchlist);
  const { BranchByUser } = useSelector((state) => state.userBranch);
  const { productList, categoryList } = useSelector((state) => state.usersetting);
  const enabled = useSelector((state) => state.settings?.branchsettings?.enableSizeColour);

  const report = SC_REPORTS.find((r) => r.code === String(routeCode || "").toUpperCase()) || SC_REPORTS[0];
  const today = dayjs().format("YYYY-MM-DD");
  const [f, setF] = useState(() => ({
    asOnDate: today,
    fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    toDate: today,
    branchID: "",
    categoryID: "",
    subCategoryID: "",
    sizeSetID: "",
    colourID: "",
    productID: "",
    variantID: "",
    search: "",
    returnType: "",
    reason: "",
    barcodeType: "",
    source: "",
  }));
  const [sizeSets, setSizeSets] = useState([]);
  const [colours, setColours] = useState([]);
  const [subCats, setSubCats] = useState([]);
  const [result, setResult] = useState(null); // { name: rows[] }
  const [tab, setTab] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState({}); // SC-40: variantID -> picked
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (branch?.guid) {
      dispatch(fetchProduct(branch));
      dispatch(fetchCategory(branch.guid));
    }
    getSizeSets().then((r) => setSizeSets(arr(r))).catch(() => {});
    getColours().then((r) => setColours(arr(r))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch?.guid]);
  useEffect(() => {
    if (!f.categoryID) return setSubCats([]);
    getSubCategories(f.categoryID).then((r) => setSubCats(arr(r))).catch(() => setSubCats([]));
  }, [f.categoryID]);
  // a new report starts empty
  useEffect(() => {
    setResult(null);
    setError("");
    setPicked({});
  }, [report.code]);

  const branches = arr(BranchByUser).map((o) => o?.branch).filter((b) => b?.guid);
  const scProducts = arr(productList).filter((p) => p.isSizeColourEnabled || arr(p.productVariants).length);
  const variants = arr(scProducts.find((p) => p.guid === f.productID)?.productVariants);
  const missing = (report.req || []).filter((k) => !f[k]);

  /* ------------------------------------------------------------ run */
  const run = async () => {
    if (missing.length) return toast.error(`Pick ${missing.map((k) => LABEL[k].toLowerCase()).join(", ")}`);
    setLoading(true);
    setError("");
    try {
      // empty filters go as null; dates only where the report reads them
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : v]));
      if (!DATED(report)) {
        body.fromDate = null;
        body.toDate = null;
      } else body.asOnDate = null;
      const res = await getSizeColourReport(report.code, body);
      const sets = Object.fromEntries(Object.entries(res || {}).filter(([, v]) => Array.isArray(v)));
      setResult(sets);
      setTab((t) => (t in sets ? t : Object.keys(sets)[0] || ""));
      setPicked({});
    } catch (e) {
      setResult(null);
      setError(errMsg(e, "The report could not be run"));
    } finally {
      setLoading(false);
    }
  };

  const rows = arr(result?.[tab]);
  const cols = useMemo(() => columnsOf(rows), [rows]);
  const totals = useMemo(() => Object.fromEntries(cols.filter((c) => c.sum).map((c) => [c.k, rows.reduce((a, r) => a + (Number(r[c.k]) || 0), 0)])), [cols, rows]);
  const kpis = cols.filter((c) => c.sum).slice(0, 4);

  /* ------------------------------------------------------------ export */
  const csv = () => {
    const cell = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.map((c) => c.t), ...rows.map((r) => cols.map((c) => (c.num ? r[c.k] ?? "" : cellText(c, r[c.k]))))];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["﻿" + lines.map((l) => l.map(cell).join(",")).join("\r\n")], { type: "text/csv;charset=utf-8" }));
    a.download = `${report.code} ${report.name} - ${titleOf(tab)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const markPrinted = async (printed) => {
    const variantIDs = Object.keys(picked).filter((k) => picked[k]);
    if (!variantIDs.length) return toast.error("Tick the barcodes first");
    try {
      await setLabelPrinted({ variantIDs, printed });
      toast.success(printed ? "Marked as printed" : "Marked as not printed");
      run();
    } catch (e) {
      toast.error(errMsg(e, "Could not update the labels"));
    }
  };

  /* ------------------------------------------------------------ render */
  const field = (k) => {
    const req = (report.req || []).includes(k);
    const sel = (opts, ph) => (
      <AxSelectField key={k} label={LABEL[k]} required={req} value={f[k]} onChange={(e) => set(k, e.target.value)}>
        <option value="">{ph}</option>
        {opts}
      </AxSelectField>
    );
    switch (k) {
      case "asOnDate":
      case "fromDate":
      case "toDate":
        return (
          <AxField key={k} label={LABEL[k]} required={req}>
            <input type="date" value={f[k]} onChange={(e) => set(k, e.target.value)} />
          </AxField>
        );
      case "branchID":
        return sel(branches.map((b) => <option key={b.guid} value={b.guid}>{b.name}</option>), "All branches");
      case "categoryID":
        return sel(arr(categoryList).map((c) => <option key={c.guid} value={c.guid}>{c.name}</option>), "All categories");
      case "subCategoryID":
        return sel(subCats.map((c) => <option key={c.guid} value={c.guid}>{c.name}</option>), f.categoryID ? "All sub-categories" : "Pick a category first");
      case "sizeSetID":
        return sel(sizeSets.map((s) => <option key={s.guid} value={s.guid}>{s.name}</option>), "All size sets");
      case "colourID":
        return sel(colours.map((c) => <option key={c.guid} value={c.guid}>{c.name}</option>), "All colours");
      case "productID":
        return (
          <AxSelectField
            key={k}
            label={LABEL[k]}
            required={req}
            value={f.productID}
            onChange={(e) => setF((x) => ({ ...x, productID: e.target.value, variantID: "" }))}
          >
            <option value="">All products</option>
            {scProducts.map((p) => (
              <option key={p.guid} value={p.guid}>
                {[p.code, p.name].filter(Boolean).join(" · ")}
              </option>
            ))}
          </AxSelectField>
        );
      case "variantID":
        return sel(
          variants.map((v) => (
            <option key={v.variantID} value={v.variantID}>
              {[v.sizeCode, v.colourName, v.sku].filter(Boolean).join(" · ")}
            </option>
          )),
          f.productID ? "Pick a variant" : "Pick a product first"
        );
      case "returnType":
        return sel(["Refund", "Exchange"].map((t) => <option key={t} value={t}>{t}</option>), "Refund and exchange");
      case "barcodeType":
        return sel(["EAN-13", "EAN-8", "Code128"].map((t) => <option key={t} value={t}>{t}</option>), "Any type");
      case "source":
        return sel(["Auto", "Manual"].map((t) => <option key={t} value={t}>{t}</option>), "Any source");
      default:
        return (
          <AxField key={k} label={LABEL[k]} required={req}>
            <input type="text" value={f[k]} onChange={(e) => set(k, e.target.value)} />
          </AxField>
        );
    }
  };
  // the report's own required fields first, then the common filters
  const fieldKeys = [
    ...(report.req || []).filter((k) => k !== "fromDate" && k !== "toDate"),
    ...(DATED(report) ? ["fromDate", "toDate"] : ["asOnDate"]),
    "branchID",
    "categoryID",
    "subCategoryID",
    "sizeSetID",
    "colourID",
    "productID",
    "variantID",
    ...(report.extra || []),
  ].filter((k, i, a) => a.indexOf(k) === i);

  const groups = [...new Set(SC_REPORTS.map((r) => r.group))];
  const sets = Object.keys(result || {});
  const labelsOn = report.labels && rows.some((r) => r.variantID);

  const body = (
    <div className="axa-detailform">
      <div className="axa-tabbody">
        {enabled === false && <AxTodoNote>Size &amp; colour is switched off in Settings › General.</AxTodoNote>}
        <div className="axsc-pickers">
          <AxSelectField label="Report" value={report.code} onChange={(e) => navigate(`${all_routes.sizecolourreport}/${e.target.value}`)}>
            {groups.map((g) => (
              <optgroup key={g} label={g}>
                {SC_REPORTS.filter((r) => r.group === g).map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.code} · {r.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </AxSelectField>
          {fieldKeys.map(field)}
          <AxField label="Search">
            <input
              type="text"
              value={f.search}
              placeholder="Item, SKU, barcode…"
              onChange={(e) => set("search", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
            />
          </AxField>
        </div>
        <div className="axsc-sum">
          <button type="button" className="axa-btn axa-btn-primary" onClick={run} disabled={loading}>
            {loading ? "Running…" : "Run report"}
          </button>
          {missing.length > 0 && <span>Needs: {missing.map((k) => LABEL[k]).join(", ")}</span>}
          <span style={{ flex: 1 }} />
          {result && rows.length > 0 && (
            <>
              <button type="button" className="axa-btn axa-btn-ghost" onClick={csv}>
                CSV
              </button>
              <button type="button" className="axa-btn axa-btn-ghost" onClick={() => window.print()}>
                Print
              </button>
            </>
          )}
          {labelsOn && (
            <>
              <button type="button" className="axa-btn axa-btn-ghost" onClick={() => markPrinted(true)}>
                Mark labels printed
              </button>
              <button type="button" className="axa-btn axa-btn-ghost" onClick={() => markPrinted(false)}>
                Not printed
              </button>
            </>
          )}
        </div>

        {error && <div className="axsc-err">{error}</div>}
        {result && (
          <>
            {sets.length > 1 && (
              <AxTabs tabs={sets.map((s) => ({ key: s, label: titleOf(s), count: result[s].length }))} active={tab} onChange={setTab} />
            )}
            {kpis.length > 0 && (
              <div className="axsc-sum">
                <span>
                  <b>{rows.length.toLocaleString("en-US")}</b> rows
                </span>
                {kpis.map((c) => (
                  <span key={c.k}>
                    {c.t}: <b>{fmtNum(totals[c.k])}</b>
                  </span>
                ))}
              </div>
            )}
            {rows.length ? (
              <div className="axsc-vwrap axsc-report">
                <table className="axsc-grid">
                  <thead>
                    <tr>
                      {labelsOn && <th />}
                      {cols.map((c) => (
                        <th key={c.k} style={c.num ? { textAlign: "right" } : undefined}>
                          {c.t}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.variantID ? `${r.variantID}-${i}` : i}>
                        {labelsOn && (
                          <td className="c">
                            {r.variantID ? (
                              <input
                                type="checkbox"
                                aria-label="Pick"
                                checked={!!picked[r.variantID]}
                                onChange={(e) => setPicked((m) => ({ ...m, [r.variantID]: e.target.checked }))}
                              />
                            ) : null}
                          </td>
                        )}
                        {cols.map((c) => (
                          <td key={c.k} style={c.num ? { textAlign: "right", fontVariantNumeric: "tabular-nums" } : undefined}>
                            {c.hex && HEX.test(r[c.k] || "") ? <span className="axsc-swatch" style={{ background: r[c.k] }} title={r[c.k]} /> : cellText(c, r[c.k])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  {Object.keys(totals).length > 0 && (
                    <tfoot>
                      <tr>
                        {labelsOn && <td />}
                        {cols.map((c, i) => (
                          <td key={c.k} style={c.num ? { textAlign: "right", fontWeight: 600 } : { fontWeight: 600 }}>
                            {c.k in totals ? fmtNum(totals[c.k]) : i === 0 ? "Total" : ""}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            ) : (
              <div className="axst-empty">Nothing matches these filters.</div>
            )}
          </>
        )}
        {!result && !error && !loading && <div className="axst-empty">Pick the filters and press Run report.</div>}
      </div>
    </div>
  );

  return (
    <AdminScreen
      title={`${report.code} · ${report.name}`}
      subtitle="Size & Colour reports"
      tools={
        <button type="button" className="axa-iconbtn" title="Run again" onClick={run} disabled={!result}>
          <AxRefreshIcon />
        </button>
      }
      detail={body}
      status={<AxStatusKeys keys={[{ k: "Enter", t: "in Search runs the report" }]} right={result ? `${rows.length} rows · ${titleOf(tab)}` : report.group} />}
    />
  );
};

export default SizeColourReports;
